'use client';

import type { PublicUserDocument } from '@models/user';
import ProductPriceRange from '@ui/ProductPriceRange';
import { getItem, logEvent } from '@util/analytics';
import { getCartItemFromProductDocument, cn } from '@util/index';
import { getProductSlug } from '@util/urlHelpers';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import type { OrderItemDocument } from 'models/order';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useShoppingCart } from 'context/ShoppingCartContext';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import { MotionDiv } from 'motion';
import {
  trackUserInteraction,
  trackUserSearch,
} from '@util/firestore/recommendations';
import { FavoriteButton } from './FavoriteButton';
import { AddToCartButton } from './AddToCartButton';
import { ProductTitle } from './ProductTitle';
import { ProductPicture } from './ProductPicture';
import ProductShippingEstimate from './ProductShippingEstimate';
import ProductFitment from './ProductFitment';
import { useQuery } from '@tanstack/react-query';
import { getPublicUserDoc } from '@util/firestore/users';

const MXProductCard = ({
  product,
  list_id,
  list_name,
  attribution,
  bgColor,
  priority,
  whiteText,
}: {
  product: ProductDocument;
  seller?: PublicUserDocument;
  list_id?: string;
  list_name?: string;
  attribution: OrderItemDocument['attribution'];
  bgColor?: string;
  priority?: boolean;
  whiteText?: boolean;
}) => {
  const { increaseCartQty, canAddProductToCart, setCartOpen } =
    useShoppingCart();
  const { setAttribution, getAttribution } = useAttributionContext();
  const pathname = usePathname();
  const { userDoc, observeProduct } = useAuth();
  const { filters } = useSearch();
  const router = useRouter();

  const { data: sellerDoc } = useQuery({
    queryKey: ['seller_doc', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
    enabled: Boolean(product.seller_id),
  });

  const handleAddToCart = () => {
    if (userDoc) {
      if (canAddProductToCart(product.id)) {
        const attribution = getAttribution(product.id);
        const cartItem = getCartItemFromProductDocument(
          product,
          null,
          attribution
        );

        increaseCartQty(cartItem);
        setCartOpen(true);
      } else {
        setCartOpen(true);
      }
    } else {
      router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
    }
  };

  const ref = useLogWhenInView(() => {
    if (userDoc?.uid) {
      const attribution = ['gear_for_you', 'parts_for_you'].includes(
        list_id ?? ''
      )
        ? {
            page: 'for_you',
            section: list_id ?? '',
            data_source: 'typesense',
          }
        : getAttribution(product.id) ?? '';
      observeProduct({
        id: product.id,
        attribution: attribution ?? '',
      });
    }
  });

  return (
    <MotionDiv
      ref={ref}
      className={cn(
        'group relative mx-auto flex h-fit w-full flex-col rounded-brand sm:mx-0 sm:w-[29rem]',
        bgColor
      )}
    >
      <Link
        href={getProductSlug(product)}
        className="flex h-full flex-col"
        onClick={() => {
          setAttribution(product.id, attribution);
          logEvent('select_item', {
            items: [getItem(product)],
            item_list_id: list_id ?? pathname,
            item_list_name: list_name ?? pathname,
          });
          if (pathname?.includes('search') || pathname?.includes('product')) {
            if (userDoc)
              trackUserInteraction({
                uid: userDoc.uid,
                pid: product.id,
                interaction: 'search_clicked',
              });
            if (filters?.term) {
              trackUserSearch({
                ...filters,
                product_clicked: product.id,
              });
            }
          }
        }}
      >
        <div className="flex h-full flex-col">
          <ProductPicture product={product} priority={priority} />

          <div
            className={cn(
              'space-y- mt-2 flex flex-grow flex-col justify-between p-[0.8rem]',
              whiteText ? 'text-white' : ''
            )}
          >
            <div className="space-y-2">
              <ProductTitle product={product} whiteText={whiteText} />

              <ProductPriceRange
                product={product}
                vertical
                textColor={whiteText ? 'white' : 'default'}
                enableCouponDiscount
              />

              <ProductShippingEstimate
                userDoc={userDoc}
                product={product}
                whiteText={whiteText}
              />

              {userDoc && !!userDoc.bikes.length && (
                <ProductFitment product={product} userDoc={userDoc} />
              )}

              {product.sold_count && product.sold_count >= 5 && (
                <span className="block text-[1.6rem] font-medium text-brand-secondary sm:text-[1.7rem]">
                  {product.sold_count} sold
                </span>
              )}

              {/* {!!product.stock &&
                product.stock <= 3 &&
                !product.has_variations && (
                  <span className="block text-[1.6rem] font-semibold text-brand-secondary sm:text-[1.7rem]">
                    {product.stock === 1
                      ? 'Only one left'
                      : `Only ${product.stock} left`}
                  </span>
                )} */}
            </div>
          </div>
        </div>
      </Link>

      <div className="absolute right-[1rem] top-[1rem] flex w-full justify-end sm:right-[1rem]">
        <FavoriteButton
          product={product}
          initialCount={product.favorite_count}
        />
      </div>

      {pathname === '/my-cart' && (
        <div className="absolute right-[1rem] top-[15rem] flex w-full cursor-pointer items-end justify-end lg:right-[2rem] lg:top-[23rem]">
          <AddToCartButton handleAddToCart={handleAddToCart} />
        </div>
      )}
    </MotionDiv>
  );
};

export default MXProductCard;
